"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {NeoButton} from "@/components/forms";
import {useNeoContext} from "@/context/neo/neo.context";
import {CustomerType} from "@/libs/enums/live";
import {activeRoute} from "@/service/routePlan.service";
import {Card, CardBody, CardHeader} from "@nextui-org/react";
import {useEffect, useMemo, useRef, useState} from "react";

type ViewUserProps = {
  client: Client;
  header: string;
  items: UserDetailsItems | null;
  onRefresh: () => Promise<void>;
};

type SelectOption = {
  value: string;
  label: string;
};

type RouteResponse = {
  rows?: Array<{
    routeId?: string | number;
    routeName?: string;
    name?: string;
  }>;
};

type VehicleMappingItem = {
  key: string;
  vehicleNo: string;
  tripType: "pickup" | "drop";
  // routeName: string;
  // boardingPoint: string;
};

export const ViewUser: React.FC<ViewUserProps> = ({
  client,
  header,
  items,
  onRefresh
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);

  const [routeOptions, setRouteOptions] = useState<SelectOption[]>([]);
  const [routeNameMap, setRouteNameMap] = useState<Record<string, string>>({});
  const [loadingRoutes, setLoadingRoutes] = useState(false);

  const hasFetchedRoutesRef = useRef(false);

  const normalizedItems = normalizeUserItems(items);

  useEffect(() => {
    if (hasFetchedRoutesRef.current) return;
    hasFetchedRoutesRef.current = true;

    const fetchRoutes = async () => {
      setLoadingRoutes(true);

      try {
        const response =
          (await activeRoute.onGetActiveRoutes()) as RouteResponse | null;

        if (!response) {
          setRouteOptions([]);
          setRouteNameMap({});
          return;
        }

        const options =
          response?.rows?.map(route => ({
            value: String(route?.routeId ?? ""),
            label: route?.routeName || route?.name || ""
          })) || [];

        const validOptions = options.filter(
          option => option.value && option.label
        );

        setRouteOptions(validOptions);

        const nextRouteNameMap = validOptions.reduce<Record<string, string>>(
          (acc, option) => {
            acc[option.value] = option.label;
            return acc;
          },
          {}
        );

        setRouteNameMap(nextRouteNameMap);
      } catch {
        setRouteOptions([]);
        setRouteNameMap({});
      } finally {
        setLoadingRoutes(false);
      }
    };

    fetchRoutes();
  }, []);

  const handleEditSuccess = async () => {
    setIsEditModalOpen(false);
    await onRefresh();
  };

  return (
    <>
      <CardBody className="border-collapse rounded-2xl bg-transparent p-0">
        <UserDetailsCard
          client={client}
          header={header}
          items={normalizedItems?.data}
          userId={normalizedItems?.userId}
          onEditUser={() => setIsEditModalOpen(true)}
          isVehicleModalOpen={isVehicleModalOpen}
          onVehicleModalChange={setIsVehicleModalOpen}
          onRefresh={onRefresh}
          routeOptions={routeOptions}
          routeNameMap={routeNameMap}
          loadingRoutes={loadingRoutes}
        />
      </CardBody>

      <NeoModal
        actionType="editUserDetails"
        onOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        hasActions={false}
        data={{
          client,
          items: normalizedItems,
          onCancel: () => setIsEditModalOpen(false),
          onSuccess: handleEditSuccess
        }}
      />
    </>
  );
};

const UserDetailsCard = ({
  client,
  header,
  items,
  userId,
  onEditUser,
  isVehicleModalOpen,
  onVehicleModalChange,
  onRefresh,
  routeOptions,
  routeNameMap,
  loadingRoutes
}: {
  client: Client;
  header: string;
  items: UserDetailsData;
  userId: string;
  onEditUser: () => void;
  isVehicleModalOpen: boolean;
  onVehicleModalChange: (open: boolean) => void;
  onRefresh: () => Promise<void>;
  routeOptions: SelectOption[];
  routeNameMap: Record<string, string>;
  loadingRoutes: boolean;
}) => {
  const {vehicles} = useNeoContext();
  const customerType = client?.customerType;

  const isEmployeeTracking = customerType === CustomerType.EMPLOYEETRACKING;

  const vehicleNameMap = useMemo(
    () => buildVehicleNameMap(vehicles, items?.vehicles),
    [vehicles, items?.vehicles]
  );

  const detailItems = [
    {label: "Name", value: items?.name},
    {label: "Gender", value: getGenderLabel(items?.gender)},
    {label: "Parent Name", value: items?.parentName},
    {label: "Phone Number", value: formatPhoneNumber(items?.phone)},
    {
      label: "Secondary Number",
      value: formatPhoneNumber(items?.userSecondaryNumber)
    },
    {label: "Email", value: items?.email},
    {
      label: isEmployeeTracking ? "Employee ID" : "Admission No",
      value: items?.admissionNo
    },
    {
      label: isEmployeeTracking ? "Department" : "Class",
      value: items?.className
    },
    ...(!isEmployeeTracking
      ? [{label: "Section", value: items?.sectionName}]
      : []),
    {label: "RFID No", value: items?.rfidNo}
  ];

  const vehicleMappingItems = buildVehicleMappingDetails(
    items?.vehicles,
    routeNameMap,
    vehicleNameMap
  );

  return (
    <div className="flex flex-col gap-6 bg-white">
      <Card className="rounded-2xl border border-default-100 bg-[#FFFFFF] shadow-transparent">
        <CardHeader className="flex items-center justify-between px-6 py-5">
          <h4 className="text-base font-semibold">User Details</h4>

          <NeoButton
            isIconOnly
            aria-label="Edit"
            className="min-w-0 bg-transparent p-0 shadow-none hover:bg-transparent"
            startContent={
              <svg
                width="18"
                height="18"
                viewBox="0 0 22 22"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.6798 3.67182L18.1554 8.16907L6.82725 19.5514L2.35426 15.0559L13.6798 3.67182ZM21.5511 2.58682L19.5553 0.581402C19.3719 0.397133 19.1539 0.250912 18.9139 0.151137C18.6738 0.0513625 18.4164 0 18.1564 0C17.8965 0 17.6391 0.0513625 17.399 0.151137C17.159 0.250912 16.941 0.397133 16.7576 0.581402L14.846 2.5026L19.3211 6.99985L21.5511 4.75896C21.8386 4.47061 22 4.08005 22 3.67289C22 3.26573 21.8386 2.87517 21.5511 2.58682ZM0.0124994 21.3772C-0.00655972 21.4618 -0.00378469 21.5498 0.0205631 21.6329C0.0449109 21.7161 0.0900299 21.7917 0.151669 21.8526C0.213309 21.9135 0.28944 21.9578 0.37289 21.9811C0.45634 22.0045 0.544362 22.0062 0.628663 21.9861L5.61555 20.7709L1.14256 16.2737L0.0124994 21.3772Z"
                  fill="black"
                />
              </svg>
            }
            onPress={onEditUser}
          />
        </CardHeader>

        <CardBody className="px-6 pb-6 pt-2">
          <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[auto,1fr]">
            <div className="flex items-start justify-start">
              <div className="grid w-full grid-cols-1 gap-1">
                <div className="col-span-1 py-2 lg:col-span-1">
                  <div className="flex h-32 w-32 items-center justify-center rounded-2xl border-2 border-solid border-default-200 bg-default-50 p-1">
                    <img
                      src={items?.image || "/images/avatar.png"}
                      alt="Profile"
                      className="h-full w-full rounded-xl object-cover text-xs"
                      onError={event => {
                        event.currentTarget.src = "/images/avatar.png";
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid w-full grid-cols-4 gap-4">
              {detailItems.map(item => (
                <div key={item.label} className="col-span-4 py-0 lg:col-span-1">
                  <ReadOnlyField label={item.label} value={item.value} />
                </div>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="rounded-2xl border border-default-100 bg-[#FFFFFF] shadow-transparent">
        <CardHeader className="flex items-center justify-between px-6 py-5">
          <h4 className="text-base font-semibold">Vehicle Mapping Details</h4>

          <NeoButton
            isIconOnly
            aria-label="Edit vehicle mapping details"
            className="min-w-0 bg-transparent p-0 shadow-none hover:bg-transparent"
            startContent={
              <svg
                width="18"
                height="18"
                viewBox="0 0 22 22"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.6798 3.67182L18.1554 8.16907L6.82725 19.5514L2.35426 15.0559L13.6798 3.67182ZM21.5511 2.58682L19.5553 0.581402C19.3719 0.397133 19.1539 0.250912 18.9139 0.151137C18.6738 0.0513625 18.4164 0 18.1564 0C17.8965 0 17.6391 0.0513625 17.399 0.151137C17.159 0.250912 16.941 0.397133 16.7576 0.581402L14.846 2.5026L19.3211 6.99985L21.5511 4.75896C21.8386 4.47061 22 4.08005 22 3.67289C22 3.26573 21.8386 2.87517 21.5511 2.58682ZM0.0124994 21.3772C-0.00655972 21.4618 -0.00378469 21.5498 0.0205631 21.6329C0.0449109 21.7161 0.0900299 21.7917 0.151669 21.8526C0.213309 21.9135 0.28944 21.9578 0.37289 21.9811C0.45634 22.0045 0.544362 22.0062 0.628663 21.9861L5.61555 20.7709L1.14256 16.2737L0.0124994 21.3772Z"
                  fill="black"
                />
              </svg>
            }
            onPress={() => onVehicleModalChange(true)}
          />
        </CardHeader>

        <CardBody className="px-6 pb-6 pt-2">
          {vehicleMappingItems.length > 0 ? (
            <div className="flex flex-col gap-6">
              {vehicleMappingItems.map(item => (
                <div
                  key={item.key}
                  className="grid w-full grid-cols-1 gap-5 border-b border-default-100 pb-2 lg:grid-cols-4"
                >
                  <ReadOnlyField label="Vehicle No" value={item.vehicleNo} />
                  <ReadOnlyField
                    label="Trip Type"
                    value={item.tripType === "pickup" ? "Pickup" : "Drop"}
                  />
                  {/* <ReadOnlyField label="Route" value={item.routeName} /> */}
                  {/* <ReadOnlyField
                    label="Boarding Point"
                    value={item.boardingPoint}
                  /> */}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-2 text-sm font-medium text-default-500">NA</div>
          )}
        </CardBody>
      </Card>

      <NeoModal
        actionType="editVehicleMappingDetails"
        onOpen={isVehicleModalOpen}
        onClose={() => onVehicleModalChange(false)}
        hasActions={false}
        data={{
          client,
          items,
          userId,
          onClose: () => onVehicleModalChange(false),
          onRefresh,
          onLocalUpdate: async () => {
            await onRefresh();
          },
          routeOptions,
          loadingRoutes
        }}
      />
    </div>
  );
};

const ReadOnlyField = ({
  label,
  value,
  className
}: {
  label: string;
  value: string | number | null | undefined;
  className?: string;
}) => {
  return (
    <div className={`flex flex-col gap-1 ${className || ""}`}>
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      <div className="font-semibold">
        <span className="break-words text-small">{value || "NA"}</span>
      </div>
    </div>
  );
};

const getGenderLabel = (gender: string | number | null | undefined) => {
  if (gender === 1 || gender === "1") return "Male";
  if (gender === 2 || gender === "2") return "Female";
  if (gender === "Male" || gender === "Female") return gender;
  return "NA";
};

const buildVehicleMappingDetails = (
  vehicles: UserVehicle[] | undefined,
  routeNameMap: Record<string, string>,
  vehicleNameMap: Record<string, string>
): VehicleMappingItem[] => {
  if (!vehicles?.length) return [];

  return vehicles.flatMap((vehicle, index) => {
    const trackerid = getVehicleTrackerId(vehicle);
    const routeId = getVehicleRouteId(vehicle);

    return getTripTypeLabels(vehicle.type).map(tripType => ({
      key: `${tripType}-${trackerid || "empty"}-${routeId || "no-route"}-${index}`,
      vehicleNo: getVehicleDisplayName(vehicle, vehicleNameMap),
      tripType,
      routeName: routeNameMap[routeId] || vehicle?.routeName || "NA"
      //boardingPoint: vehicle?.bpName || "NA"
    }));
  });
};

const buildVehicleNameMap = (
  lookupVehicles: VehicleInfo[] | undefined,
  userVehicles: UserVehicle[] | undefined
) => {
  const vehicleNameMap: Record<string, string> = {};

  const addVehicleName = (
    trackerid: string,
    trackerName: string | undefined,
    shouldOverwrite = false
  ) => {
    const readableTrackerName = getReadableTrackerName(trackerName, trackerid);

    if (!trackerid || !readableTrackerName) return;

    if (shouldOverwrite || !vehicleNameMap[trackerid]) {
      vehicleNameMap[trackerid] = readableTrackerName;
    }
  };

  userVehicles?.forEach(vehicle =>
    addVehicleName(getVehicleTrackerId(vehicle), vehicle.trackerName)
  );

  lookupVehicles?.forEach(vehicle =>
    addVehicleName(String(vehicle.trackerId), vehicle.trackerName, true)
  );

  return vehicleNameMap;
};

const getVehicleDisplayName = (
  vehicle: UserVehicle,
  vehicleNameMap: Record<string, string>
) => {
  const trackerid = getVehicleTrackerId(vehicle);

  return (
    vehicleNameMap[trackerid] ||
    getReadableTrackerName(vehicle.trackerName, trackerid) ||
    trackerid ||
    "NA"
  );
};

const getReadableTrackerName = (
  trackerName: string | undefined,
  trackerid: string
) => {
  const readableTrackerName = String(trackerName ?? "").trim();

  if (!readableTrackerName || readableTrackerName === trackerid) return "";

  return readableTrackerName;
};

const getVehicleTrackerId = (vehicle: UserVehicle) => {
  return String(vehicle?.trackerid ?? "");
};

const getVehicleRouteId = (vehicle: UserVehicle) => {
  return String(vehicle?.routeId ?? "");
};

const getTripTypeLabels = (type: UserVehicleType | undefined) => {
  if (type === 1) return ["pickup" as const];
  if (type === 2) return ["drop" as const];

  return ["pickup" as const, "drop" as const];
};

const emptyUserDetailsData: UserDetailsData = {
  userId: "",
  name: "",
  gender: "",
  parentName: "",
  phone: "",
  email: "",
  className: "",
  sectionName: "",
  admissionNo: "",
  rfidNo: "",
  vehicles: []
};

const normalizeUserItems = (
  items: UserDetailsItems | null | undefined
): UserDetailsItems => {
  const data = items?.data || emptyUserDetailsData;

  return {
    userId: items?.userId || data.userId || "",
    data: {
      ...emptyUserDetailsData,
      ...data,
      image: data.image || data.profileImage,
      userSecondaryNumber:
        data.userSecondaryNumber || data.secondaryNumber || ""
    }
  };
};

const formatPhoneNumber = (value: string | null | undefined) => {
  if (!value) return "NA";
  return value;
};
